import { getStackTrace, log } from '@src/Log.init';
import { uploadImageFile } from './imageAPI';
import { rootApi } from './root.api';

function mapRequest(body) {
  let request = { ...body };
  if (body.clientId) request.client_id = body.clientId;
  if (body.tenantId) request.tenant_id = body.tenantId;
  return request;
}

function mapResponse(results) {
  let response = null;
  if (results instanceof Array) {
    response = [];
    results.forEach((tenant) => response.push(mapTenant(tenant)));
  } else {
    response = mapTenant(results);
  }
  return response;
}

function mapTenant(tenant) {

  let tenantData = { ...tenant };
  if (tenant && tenant.client_id) tenantData.clientId = tenant.client_id;
  if (tenant && tenant.tenant_id) tenantData.tenantId = tenant.tenant_id;

  return tenantData;
}

export const clientApi = rootApi.injectEndpoints({
  tagTypes: ['Client'],
  endpoints: (builder) => ({
    getTenantClient: builder.query({
      query: () => ({
        url: `tenantclient`,
        method: 'GET',
      }),
      providesTags: ['Client'],
      asyncOnQueryStarted: async (arg, { queryFulfilled }) => {
        log.debug({
          message_type: 'Client',
          message: 'getTenantClient: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Client',
            message: 'getTenantClient: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getTenantClient: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse: (response) => {
        log.debug({
          message_type: 'Client',
          message: 'getTenantClient: transformResponse',
          data: response,
        });

        return mapResponse(response);
      },
    }),
    getClients: builder.query({
      query: () => ({
        url: `client`,
        method: 'GET',
      }),
      providesTags: ['Client'],
      asyncOnQueryStarted: async (arg, { queryFulfilled }) => {
        log.debug({
          message_type: 'Client',
          message: 'getClients: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Client',
            message: 'getClients: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getClients: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse: (response) => {
        log.debug({
          message_type: 'Client',
          message: 'getTenantClient: transformResponse',
          data: response,
        });

        return mapResponse(response);
      },
    }),
    getClientStats: builder.query({
      query: () => ({
        url: `clientstats`,
        method: 'GET',
      }),
      providesTags: ['Client'],
      asyncOnQueryStarted: async (arg, { queryFulfilled }) => {
        log.debug({
          message_type: 'Client',
          message: 'getClientStats: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Client',
            message: 'getClientStats: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getClientStats: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
    }),
    getClientTenants: builder.query({
      query: (clientId) => ({
        url: `clienttenants/${clientId}`,
        method: 'GET',
      }),
      providesTags: ['Client'],
      asyncOnQueryStarted: async (arg, { queryFulfilled }) => {
        log.debug({
          message_type: 'Client',
          message: 'getClientTenants: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Client',
            message: 'getClientTenants: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'getClientTenants: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse: (response) => {
        log.debug({
          message_type: 'Client',
          message: 'getClientTenants: transformResponse',
          data: response,
        });

        return mapResponse(response);
      },
    }),
    createClient: builder.mutation({
      async queryFn(client, { getState, dispatch }, extraOptions, baseQuery) {
        try {
          let logoFile = null;
          // Store logo file and set logo to null for initial client creation
          if (client.company.logo && typeof client.company.logo !== 'string') {
            logoFile = client.company.logo; // remember the logo file
            client.company.logo = null; // clear the logo for create
          }

          // Prepare request body without the logo
          const body = {
            logo: null,
            name: client.company.name,
            start_date: client.company.startDate,
            end_date: client.company.endDate,
            billing_contact: {
              full_name: client.billing.name,
              phone_number: client.billing.phone,
              email_address: client.billing.email,
            },
            administrative_contact: {
              full_name: client.administrator.name,
              phone_number: client.administrator.phone,
              email_address: client.administrator.email,
            },
            production_environments: client.environments.productionCount,
            test_environments: client.environments.testCount,
          };

          // Log the creation attempt
          log.debug({
            message_type: 'Client',
            message: 'createClient: pending',
          });

          // First create the client without the logo
          const initialResult = await baseQuery({
            url: 'client',
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          });

          if (initialResult.error) {
            throw initialResult.error;
          }

          let response = initialResult.data;

          // If we have a logo file, upload it and update the client
          if (logoFile !== null) {
            try {
              // Upload the logo and get the URL
              response.logo = await uploadImageFile(response._id, logoFile);

              // Update the client with the logo URL
              const updateResult = await baseQuery({
                url: 'client',
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: mapRequest(response),
              });

              if (updateResult.error) {
                throw updateResult.error;
              }

              response = updateResult.data;
            } catch (error) {
              log.error({
                message_type: 'SystemError',
                message: 'Logo upload failed',
                meta_data: getStackTrace(),
                error,
              });
              // Continue without logo if upload fails, but don't fail the whole operation
            }
          }

          log.info({
            message_type: 'Client',
            message: 'createClient: fulfilled',
            data: response,
          });

          return { data: mapResponse(response) };
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'createClient: failure',
            meta_data: getStackTrace(),
            error,
          });
          return { error };
        }
      },
      invalidatesTags: ['Client'],
    }),
    updateClient: builder.mutation({
      query: (client) => ({
        url: `client`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: mapRequest(client),
      }),
      invalidatesTags: ['Client'],
      asyncOnQueryStarted: async (arg, { queryFulfilled }) => {
        log.debug({
          message_type: 'Client',
          message: 'updateClient: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Client',
            message: 'updateClient: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'updateClient: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse: (response) => {
        log.debug({
          message_type: 'Client',
          message: 'updateClient: transformResponse',
          data: response,
        });
        return mapResponse(response);
      },
    }),
    updateClientTenant: builder.mutation({
      query: (tenant) => ({
        url: `clienttenant`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: mapRequest(tenant),
      }),
      invalidatesTags: ['Client'],
      asyncOnQueryStarted: async (arg, { queryFulfilled }) => {
        log.debug({
          message_type: 'Client',
          message: 'updateClientTenant: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Client',
            message: 'updateClientTenant: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'updateClientTenant: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse: (response) => {
        log.debug({
          message_type: 'Client',
          message: 'updateClientTenant: transformResponse',
          data: response,
        });
        return mapResponse(response);
      },
    }),
    addClientTenant: builder.mutation({
      query: (tenant) => ({
        url: `clienttenant`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: mapRequest(tenant),
      }),
      invalidatesTags: ['Client'],
      asyncOnQueryStarted: async (arg, { queryFulfilled }) => {
        log.debug({
          message_type: 'Client',
          message: 'addClientTenant: pending',
        });
        try {
          const result = await queryFulfilled;
          log.info({
            message_type: 'Client',
            message: 'addClientTenant: fulfilled',
            data: result.data,
          });
        } catch (error) {
          log.error({
            message_type: 'SystemError',
            message: 'addClientTenant: failure',
            meta_data: getStackTrace(),
            error,
          });
        }
      },
      transformResponse: (response) => {
        log.debug({
          message_type: 'Client',
          message: 'addClientTenant: transformResponse',
          data: response,
        });
        return mapResponse(response);
      },
    }),
  }),
});

export const {
  useGetTenantClientQuery,
  useLazyGetTenantClientQuery,
  useGetClientsQuery,
  useLazyGetClientsQuery,
  useGetClientStatsQuery,
  useLazyGetClientStatsQuery,
  useGetClientTenantsQuery,
  useLazyGetClientTenantsQuery,
  useCreateClientMutation,
  useUpdateClientMutation,
  useUpdateClientTenantMutation,
  useAddClientTenantMutation,
} = clientApi;
